/**
 * @lines98/client/components/mode-card — ЛОББИ-ветка карточки режима «Играть»
 * (extra-modes §5.1). ТОНКАЯ ОБЁРТКА над примитивом `board-preview-card`.
 *
 * ЗАЧЕМ / WHY (owner-directive 2026-06-25, тикет card-preview-primitive): оболочка
 * карточки (хром + живая мини-доска + локализованное имя + paint-изоляция) вынесена в
 * ОБЩИЙ примитив `components/board-preview-card` — ОДИН источник для лобби И стартовой
 * (модель «ствол → ветки»). Лобби-ветка лишь ДОБАВЛЯЕТ своё: «?»-инфо-попап пояснения
 * (справа от имени) + клик-в-игру (whole-card select). Раньше mode-card сам строил доску/
 * имя/хром — теперь это примитив; здесь остаётся ТОЛЬКО лобби-специфика.
 *
 * Owner-vision (2026-06-03): плашка = ПОДСТАВКА, на которой лежит мини-доска под режим;
 * под доской ОДНА строка [ имя + «?» ]; ПОЯСНЕНИЕ режима живёт в «?» (popover), НЕ
 * отдельной видимой строкой. Счёт/ходы на превью НЕ показываем.
 *
 * Карта — `<div role="button">` (НЕ `<button>`: вложенная «?»-кнопка = invalid nested
 * button). Клик/Enter/Space по карте → onSelect (unlocked). «?»-триггер сам делает
 * stopPropagation (info-popover) → клик по «?» не запускает режим. Locked → бейдж «Скоро»
 * (рисует примитив) + без onSelect.
 *
 * Spec: extra-modes-alltime.md §2.1/§5.1/§5.2 + SKILL §0.B/§8. Хром/доска/имя/paint —
 *   components/board-preview-card(.css); строка имени + «?» позиционируются в mode-card.css.
 */

import './mode-card.css';

import { createBoardPreviewCard } from './board-preview-card.js';
import { mountInfoPopover, type InfoPopoverHandle } from './info-popover.js';
import { getLocale } from '../lib/i18n/index.js';
import type { ModeEntry } from '../lib/mode-registry.js';
import type { ContentLang } from '@lines98/shared/config/seo';

export interface ModeCardOptions {
  /** Registry entry — единый источник имени/уровня подсказки/locked/badge (передаём в примитив). */
  readonly entry: ModeEntry;
  /** Пояснение режима — живёт в «?» popover (НЕ видимая строка). */
  readonly hint: string;
  /** Accessible label for the whole-card target. */
  readonly ariaLabel?: string;
  /** Invoked on card select (UNLOCKED only — locked cards never call this). */
  readonly onSelect: () => void;
}

export interface ModeCardHandle {
  /** The card root (`<div role=button>`) — lobby appends it to a family grid. */
  readonly element: HTMLElement;
  destroy(): void;
}

/**
 * Build one lobby mode card. Монтирует общий примитив `board-preview-card` (хром + живая
 * мини-доска + локализованное имя + paint-изоляция) и ДОБАВЛЯЕТ лобби-специфику: «?»-попап
 * пояснения в строку имени + клик-в-игру. Имя локализуется в примитиве через `modeLabel`
 * по ГЛОБАЛЬНОМУ locale (лобби ремонтируется на смену языка → getLocale актуален).
 */
export function createModeCard(opts: ModeCardOptions): ModeCardHandle {
  const entry = opts.entry;
  const locked = !!entry.locked;

  // Общая оболочка (ствол): хром + доска + имя + paint-изоляция (один источник).
  const card = createBoardPreviewCard({
    entry,
    lang: getLocale() as ContentLang,
    variant: 'mode-card--lobby',
  });
  const el = card.element;
  el.setAttribute('aria-label', opts.ariaLabel ?? card.name + ': ' + opts.hint);
  if (locked) {
    el.setAttribute('aria-disabled', 'true');
  } else {
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
  }

  // Лобби-специфика 1: «?»-попап пояснения справа от имени (§0.E).
  const qHost = document.createElement('span');
  qHost.className = 'mode-card-q';
  card.nameRowEl.appendChild(qHost);
  const popover: InfoPopoverHandle = mountInfoPopover({
    title: card.name,
    bodyText: opts.hint,
    triggerHost: qHost,
    variant: '?',
  });

  // Лобби-специфика 2: whole-card select (unlocked). «?»-триггер сам stopProp →
  // клик по «?» не стартует режим. Locked → без wiring.
  let onClick: (() => void) | null = null;
  let onKey: ((e: KeyboardEvent) => void) | null = null;
  if (!locked) {
    onClick = (): void => opts.onSelect();
    el.addEventListener('click', onClick);
    onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        opts.onSelect();
      }
    };
    el.addEventListener('keydown', onKey);
    // Press-feedback (owner-request 2026-06-28): мягкая просадка-depress
    // (.press-feedback → scale(0.98) для .board-preview-card, press-feedback.css) —
    // единый отклик на нажатие (owner-final 2026-06-28: ripple-волна отвергнута совсем,
    // depress даёт тактильное подтверждение без перф-стоимости).
    // ТОЛЬКО unlocked: locked-карточка не выбирается.
    el.classList.add('press-feedback');
  }

  return {
    element: el,
    destroy(): void {
      popover.unmount();
      if (onClick) el.removeEventListener('click', onClick);
      if (onKey) el.removeEventListener('keydown', onKey);
      card.destroy();
    },
  };
}
